// Legacy secure backup (.ICRM) — format produced by VÉRO core exportSecureBackup():
//   { encrypted:true, type:'insurance-crm-secure-backup', version:1, salt, iv, ciphertext }
//   PBKDF2-SHA256 (150 000 iterations) → AES-GCM-256, all fields base64.

export interface LegacySecurePayload { encrypted: true; type?: string; version?: number; salt: string; iv: string; ciphertext: string }

export const isLegacySecure = (v: unknown): v is LegacySecurePayload =>
  !!v && typeof v === 'object' && (v as LegacySecurePayload).encrypted === true && typeof (v as LegacySecurePayload).ciphertext === 'string'
  && typeof (v as LegacySecurePayload).iv === 'string' && typeof (v as LegacySecurePayload).salt === 'string' && !('kdf' in (v as object));

export function b64encode(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
export function b64decode(str: string): Uint8Array<ArrayBuffer> {
  const bin = atob(str);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function deriveLegacyKey(password: string, salt: Uint8Array<ArrayBuffer>, iterations = 150000): Promise<CryptoKey> {
  const subtle = globalThis.crypto.subtle;
  const mat = await subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveKey']);
  return subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, mat, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

export async function decryptLegacy(payload: LegacySecurePayload, password: string): Promise<string> {
  const key = await deriveLegacyKey(password, b64decode(payload.salt));
  const plain = await globalThis.crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64decode(payload.iv) }, key, b64decode(payload.ciphertext));
  return new TextDecoder().decode(plain);
}

/** Byte-compatible with the legacy encryptText() — used by tests and by tools that re-create legacy fixtures. */
export async function encryptLegacy(text: string, password: string): Promise<LegacySecurePayload> {
  const salt = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveLegacyKey(password, salt);
  const cipher = await globalThis.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(text));
  return { encrypted: true, type: 'insurance-crm-secure-backup', version: 1, salt: b64encode(salt), iv: b64encode(iv), ciphertext: b64encode(cipher) };
}
